import { AudioLines, Download } from 'lucide-react';

import { Button, Card, Pill } from '../../components/ui';
import { MISSING_LABELS, describeMissing, useCharacterAvatarUrl } from '../../hooks';
import { CHARACTER_CATEGORY_LABELS, type Character } from '../../types/api';
import { CharacterAvatar } from './CharacterAvatar';
import { shortLanguage, voiceLabel } from './lib';
import { teamLabel } from '../../lib/team';
import styles from './Characters.module.css';

interface Props {
  character: Character;
  onOpen: (character: Character) => void;
}

export function CharacterCard({ character, onOpen }: Props) {
  // Ссылка подписанная и живёт недолго, поэтому просим её по клику, а не заранее
  // для каждой карточки в сетке.
  const avatar = useCharacterAvatarUrl(character.id, false);

  const download = async () => {
    const link = avatar.data ?? (await avatar.refetch()).data;
    if (link?.url) window.open(link.url, '_blank', 'noopener');
  };

  const voice = voiceLabel(character);
  const missing = describeMissing(character);
  // В пилюлю влезает одна причина. Остальные — в подсказку: перечисление из трёх
  // пунктов обрезалось на полуслове, и было непонятно, чего же не хватает.
  const missingList = character.missing ?? [];
  const [firstMissing = ''] = missingList;
  const missingShort =
    missingList.length > 1
      ? `${MISSING_LABELS[firstMissing] ?? firstMissing} и ещё ${missingList.length - 1}`
      : missing;

  return (
    <Card className={`${styles.card} ${character.is_active ? '' : styles.cardArchived}`}>
      <div className={`${styles.frame} ${character.is_active ? '' : styles.frameArchived}`}>
        <CharacterAvatar character={character} kind="frame" />
        <span className={character.is_active ? styles.statePillOk : styles.statePill}>
          {character.is_active ? 'активен' : 'в архиве'}
        </span>
        <div className={styles.frameCaption}>
          <div className={styles.frameCaptionText}>
            <div className={styles.frameName} title={character.name}>
              {character.name}
            </div>
            <div className={styles.frameSub}>
              {shortLanguage(character.language)}
              {character.team ? ` · ${teamLabel(character.team)}` : ''}
              {character.author_login ? ` · ${character.author_login}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div
          className={voice.muted ? styles.voiceLineMuted : styles.voiceLine}
          title={voice.text}
        >
          <AudioLines size={13} strokeWidth={1.5} aria-hidden="true" />
          <span className={styles.voiceName}>{voice.text}</span>
        </div>

        <div className={styles.pills}>
          <Pill>{shortLanguage(character.language)}</Pill>
          {character.category && <Pill>{CHARACTER_CATEGORY_LABELS[character.category]}</Pill>}
          {character.ready ? (
            <Pill tone="ok">готов к работе</Pill>
          ) : (
            <Pill tone="warn" title={missing || undefined}>{missingShort || 'не готов'}</Pill>
          )}
        </div>

        <div className={styles.cardFoot}>
          <span className={styles.cardFootMeta}>
            {character.voices.length > 0 ? `${character.voices.length} голос.` : 'без голосов'}
          </span>
          {character.has_avatar && (
            <Button
              size="xs"
              iconOnly
              className={styles.pushRight}
              title="Скачать видео персонажа"
              aria-label="Скачать видео персонажа"
              disabled={avatar.isFetching}
              onClick={() => void download()}
            >
              <Download size={13} strokeWidth={1.6} />
            </Button>
          )}
          <Button
            size="xs"
            className={character.has_avatar ? undefined : styles.pushRight}
            onClick={() => onOpen(character)}
          >
            Открыть
          </Button>
        </div>
      </div>
    </Card>
  );
}
